import { getRelativeUrl } from "@/utils/getRelativeUrl";
import { Button, type ButtonProps } from "@repo/skapa";
import { useLinkClickHandler } from "react-router-dom";

interface LinkButtonProps extends Omit<ButtonProps, "href" | "onClick"> {
  /** The route to navigate to */
  to: string;
  /** Optional click handler (analytics, etc.) */
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
}

/**
 * Renders a Button that behaves like a link (`<a>` element).
 * - Uses React Router for SPA navigation
 * - Keeps `href` for right-click / new-tab support
 * - Allows custom onClick (analytics, etc.)
 */

export const LinkButton = ({ to, onClick, ...props }: LinkButtonProps) => {
  const href = getRelativeUrl(to);
  const linkHandler = useLinkClickHandler<HTMLAnchorElement>(to);

  const handleClick: React.MouseEventHandler<HTMLAnchorElement> = (event) => {
    onClick?.(event);
    if (!event.defaultPrevented) {
      linkHandler(event);
    }
  };

  // TypeScript workaround: cast to anchor branch of ButtonAttributes
  return (
    <Button
      {...props}
      href={href}
      onClick={
        handleClick as unknown as React.MouseEventHandler<HTMLButtonElement>
      }
    />
  );
};
